import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../../../context/AppContext';
import GradientPainter from '../../common/GradientPainter/GradientPainter';
import Logo from '../../common/Logo/Logo';
import useAppSettings from '../../../hooks/useAppSettings';
import scrollBg from '../../../assets/images/ascend-scrolling-bg.svg';
import useAnalytics from '../../../hooks/useAnalytics';

import './Game1.scss';

const Game1 = () => {
  const { actions } = useApp();
  const [gameState, setGameState] = useState('intro');
  const [taps, setTaps] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [countdownNum, setCountdownNum] = useState(3);
  const [floor, setFloor] = useState(1);
  const [rank, setRank] = useState('Specialist');
  const [gameEndReason, setGameEndReason] = useState('time');
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const { homepageData } = useAppSettings();
  const hasTrackedRef = useRef(false);
  const { trackAscendLoad, trackAscendStart, trackAscendFinish, trackAscendPlayAgain } = useAnalytics();
  const TOTAL_TAPS = 200;
  const MAX_FLOOR = 62;
  const GAME_DURATION = 30;

  const lastTapTime = useRef(Date.now());
  const descentIntervalRef = useRef(null);

  const DESCENT_DELAY = 250;
  const DESCENT_RATE = 200;
  const DESCENT_AMOUNT = 2;

  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackAscendLoad();
      hasTrackedRef.current = true;
    }
  }, [trackAscendLoad]);

  const handleBackToHome = () => {
    setIsPaused(false);
    actions.setView('home');
  };

  const openExitPopup = () => {
    setShowExitPopup(true);
    setIsPaused(true);
  };

  const closeExitPopup = () => {
    setShowExitPopup(false);
    setIsPaused(false);
  };

  const getRank = (floor) => {
    if (!floor || floor < 1) return 'Specialist 1-4';
    if (floor >= 1 && floor <= 4) return 'Specialist 1-4';
    if (floor >= 5 && floor <= 8) return 'Sergeant';
    if (floor >= 9 && floor <= 12) return 'Technical Sergeant';
    if (floor >= 13 && floor <= 16) return 'Master Sergeant';
    if (floor >= 17 && floor <= 20) return 'Senior Master Sergeant';
    if (floor >= 21 && floor <= 24) return 'Chief Master Sergeant';
    if (floor >= 25 && floor <= 28) return '2nd Lieutenant';
    if (floor >= 29 && floor <= 32) return '1st Lieutenant';
    if (floor >= 33 && floor <= 36) return 'Captain';
    if (floor >= 37 && floor <= 40) return 'Major';
    if (floor >= 41 && floor <= 44) return 'Lieutenant Colonel';
    if (floor >= 45 && floor <= 48) return 'Colonel';
    if (floor >= 49 && floor <= 52) return 'Brigadier General';
    if (floor >= 53 && floor <= 56) return 'Major General';
    if (floor >= 57 && floor <= 61) return 'Lieutenant General';
    if (floor >= 62) return 'General';
    return 'Specialist 1-4';
  };

  const calculateFloor = useCallback(
    (tapCount) => {
      const progress = Math.min(tapCount / TOTAL_TAPS, 1);
      return Math.min(Math.floor(progress * MAX_FLOOR) + 1, MAX_FLOOR);
    },
    [TOTAL_TAPS, MAX_FLOOR]
  );

  const handleTap = useCallback(() => {
    if (gameState === 'playing' && !isPaused) {
      lastTapTime.current = Date.now();

      setTaps((prev) => {
        const newTaps = prev + 1;
        const newFloor = calculateFloor(newTaps);
        setFloor(newFloor);
        setRank(getRank(newFloor));

        if (newTaps >= TOTAL_TAPS) {
          setGameEndReason('completed');
          setTimeout(() => setGameState('ended'), 500);
        }

        return newTaps;
      });
    }
  }, [gameState, calculateFloor, TOTAL_TAPS, isPaused]);

  const startGame = () => {
    trackAscendStart();
    setGameState('countdown');
    setTaps(0);
    setFloor(1);
    setRank('Specialist');
    setTimeLeft(GAME_DURATION);
    setCountdownNum(3);
    setGameEndReason('time');
    lastTapTime.current = Date.now();
  };

  const playAgain = () => {
    trackAscendPlayAgain();
    setGameState('countdown');
    setTaps(0);
    setFloor(1);
    setRank('Specialist');
    setTimeLeft(GAME_DURATION);
    setCountdownNum(3);
    setGameEndReason('time');
    lastTapTime.current = Date.now();
  };

  useEffect(() => {
    if (gameState === 'playing' && !isPaused) {
      descentIntervalRef.current = setInterval(() => {
        const timeSinceLastTap = Date.now() - lastTapTime.current;

        if (timeSinceLastTap >= DESCENT_DELAY) {
          setTaps((prev) => {
            const newTaps = Math.max(0, prev - DESCENT_AMOUNT);
            const newFloor = calculateFloor(newTaps);
            setFloor(newFloor);
            setRank(getRank(newFloor));
            return newTaps;
          });
        }
      }, DESCENT_RATE);

      return () => {
        if (descentIntervalRef.current) {
          clearInterval(descentIntervalRef.current);
        }
      };
    }
  }, [gameState, calculateFloor, DESCENT_DELAY, DESCENT_RATE, DESCENT_AMOUNT, isPaused]);

  useEffect(() => {
    if (gameState === 'countdown' && !isPaused) {
      const timer = setTimeout(() => {
        if (countdownNum > 1) {
          setCountdownNum(countdownNum - 1);
        } else if (countdownNum === 1) {
          setCountdownNum('Tap!');
          setTimeout(() => {
            setGameState('playing');
            lastTapTime.current = Date.now();
          }, 1000);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [gameState, countdownNum, isPaused]);

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0 && !isPaused) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (gameState === 'playing' && timeLeft === 0 && !isPaused) {
      setGameEndReason('time');
      setGameState('ended');
      if (descentIntervalRef.current) {
        clearInterval(descentIntervalRef.current);
      }
    }
    if (gameState === 'ended') {
      trackAscendFinish();

    }

  }, [gameState, timeLeft, isPaused]);

  const progress = Math.min(taps / TOTAL_TAPS, 1);
  const playerPosition = progress * 100;
  const backgroundPosition = (1 - progress) * 100;
  const gradientTransform = progress * 400;

  const generateZigzagPath = () => {
    const segments = 100;
    const amplitude = 20;
    let path = 'M 0 0';
    for (let i = 1; i <= segments; i++) {
      const y = (i / segments) * 120;
      const baseX = i % 2 === 0 ? amplitude : -amplitude;
      const waveOffset = Math.sin((i / segments) * Math.PI * 4) * 5;
      const x = baseX + waveOffset;
      path += ` L ${x} ${y}`;
    }
    return path;
  };

  const getEndScreenContent = () => {
    if (floor >= 62) {
      return {
        title: 'Mission Complete!',
        description: 'Good job, Guardian.'
      };
    }

    if (floor >= 41 && floor <= 61) {
      return {
        title: homepageData?.ascend_fail_state_title || "Time's Up!",
        description: 'Almost there, Guardian.'
      };
    }

    if (floor >= 21 && floor <= 40) {
      return {
        title: homepageData?.ascend_fail_state_title || "Time's Up!",
        description: 'Not bad, Guardian.'
      };
    }

    return {
      title: homepageData?.ascend_fail_state_title || "Time's Up!",
      description: 'Keep trying, Guardian.'
    };
  };

  const endScreenContent = getEndScreenContent();

  return (
    <div className="game1">
      <GradientPainter />

      <div className="game1__container">
        {(gameState === 'countdown' || gameState === 'playing') && (
          <>
            <div
              className="background-journey"
              style={{
                backgroundImage: `url(${homepageData?.ascend_journey_bg?.url || scrollBg})`,
                backgroundPositionY: `${backgroundPosition}%`
              }}
            />
            <div
              className="background-gradient"
              style={{ transform: `translateY(${gradientTransform}vh)` }}
            />
          </>
        )}

        <div
          className={`ascend-game ${gameState === 'countdown' || gameState === 'playing' ? 'gameplay-active' : 'gradient-paintt'}`}
          onClick={handleTap}
        >
          {gameState === 'intro' && (
            <div className="ascend-intro">
              <div className="slides-topbar-main game-screen">
                <div className="logo-group logo-group--main">
                  <div className="logo logo-main-container">
                    <Logo
                      className="logo-main"
                      src={
                        homepageData?.level_62_logo_white?.url ||
                        homepageData?.level_62_logo_white
                      }
                      alt={
                        homepageData?.level_62_logo_white?.alt ||
                        'Level 62 Logo'
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="ascent-intro-copy">
                <h1 className="ascend-title">Ascend</h1>
                <p className="ascend-description">
                  {homepageData?.ascend_intro_screen_copy_1}
                </p>
                <p className="ascend-description">
                  {homepageData?.ascend_intro_screen_copy_2}
                </p>
                <div className="ascend-image">
                  <div className="icon-box"></div>
                </div>
                <p className="ascend-instructions">
                  {homepageData?.ascend_intro_screen_instructions_1}
                </p>
                <p className="ascend-instructions">
                  {homepageData?.ascend_intro_screen_instructions_2}
                </p>
              </div>
              <div className="game-navigation">
                <button className="btn-primary" onClick={handleBackToHome}>
                  Back
                </button>
                <button className="btn-primary inverted" onClick={startGame}>
                  Start
                </button>
              </div>
            </div>
          )}

          {(gameState === 'countdown' || gameState === 'playing') && (
            <>
              <div className="ascend-ui">
                <div className="ui-left">
                  <div className="ui-item">
                    <div className="ui-label">TIME LEFT</div>
                    <div
                      className={`ui-value ${timeLeft <= 5 ? 'time-critical' : ''}`}
                    >
                      :{timeLeft}s
                    </div>
                  </div>
                  <div className="ui-item">
                    <div className="ui-label">Rank</div>
                    <div className="ui-value rank">{rank}</div>
                  </div>
                  <div className="ui-item">
                    <div className="ui-label">Miles</div>
                    <div className="ui-value">{floor}</div>
                  </div>
                </div>
                <div className="ui-right">
                  <button
                    className="exit-button"
                    onClick={openExitPopup}
                  >
                    Exit X
                  </button>
                </div>
              </div>

              <div className="ascend-gameplay">
                {gameState === 'countdown' && (
                  <div className="ascend-countdown">
                    <div className="countdown-number">{countdownNum}</div>
                  </div>
                )}

                <div className="ascend-game-area">
                  <div className="elevator-shaft">
                    <svg
                      className="ground-platform"
                      style={{
                        bottom: `${Math.max(-29, -playerPosition * 2)}px`,
                        opacity: Math.max(0, 1 - playerPosition / 20)
                      }}
                      xmlns="http://www.w3.org/2000/svg"
                      width="120"
                      height="15"
                      viewBox="0 0 187 24"
                      fill="none"
                    >
                      <path
                        d="M7.93445 1.87661C8.39245 0.742506 9.49308 0 10.7162 0H176.284C177.507 0 178.608 0.742509 179.066 1.87661L186.335 19.8766C187.131 21.8485 185.68 24 183.553 24H3.44695C1.32031 24 -0.131133 21.8485 0.665215 19.8766L7.93445 1.87661Z"
                        fill="#FFEADF"
                      />
                    </svg>

                    <div className="elevator-line outer-left-line"></div>
                    <div className="elevator-line left-line"></div>
                    <div className="elevator-line right-line"></div>
                    <div className="elevator-line outer-right-line"></div>

                    <svg
                      className="zigzag-line"
                      viewBox="-30 0 60 100"
                      preserveAspectRatio="none"
                    >
                      <path
                        d={generateZigzagPath()}
                        stroke="rgba(255,255,255,0.4)"
                        strokeWidth="1"
                        fill="none"
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>

                    <div
                      className="player-square"
                      style={{ bottom: `calc(58px + ${playerPosition}%)` }}
                    >
                      <div className="player-svg-wrapper">
                        <svg
                          width="242"
                          height="98"
                          viewBox="0 0 242 98"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="player-svg"
                        >
                          <defs>
                            <clipPath id="clip1_1393_1057">
                              <rect
                                width="33"
                                height="48"
                                fill="white"
                                transform="translate(105 25)"
                              />
                            </clipPath>
                          </defs>
                          <rect
                            x="1"
                            y="1"
                            width="240"
                            height="96"
                            rx="45"
                            fill="white"
                            fillOpacity="0.2"
                            className="blur-rect"
                          />
                          <g>
                            <path
                              d="M42.373 2H40.2725L70.627 96H72.7275L42.373 2Z"
                              fill="white"
                              fillOpacity="0.8"
                            />
                            <path
                              d="M201.728 2H199.627L169.272 96H171.373L201.728 2Z"
                              fill="white"
                              fillOpacity="0.8"
                            />
                            <g clipPath="url(#clip1_1393_1057)">
                              <path
                                d="M121.503 25L105.414 72.98L121.503 63.3981L137.592 72.9738L121.503 25ZM107.264 70.6813L121.503 28.2329L135.739 70.6813L121.503 62.2058L107.264 70.6813ZM122.189 55.2114L121.503 52.8298L120.815 55.2114L118.418 55.8967L120.815 56.582L121.503 58.9667L122.189 56.582L124.585 55.8967L122.189 55.2114ZM112.628 64.2924L117.115 58.8561L120.642 35.6574L110.046 67.3378L120.642 61.0134L118.097 59.5045L112.628 64.2924ZM122.362 35.6574L125.888 58.8561L130.375 64.2924L124.906 59.5045L122.362 61.0134L132.958 67.3378L122.362 35.6574Z"
                                fill="white"
                              />
                            </g>
                            <rect
                              x="1"
                              y="1"
                              width="240"
                              height="96"
                              rx="45"
                              stroke="white"
                              strokeOpacity="0.8"
                              strokeWidth="2"
                            />
                          </g>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {gameState === 'ended' && (
            <div className="ascend-end">
              <div className="slides-topbar-main game-screen">
                <div className="logo-group logo-group--main">
                  <div className="logo logo-main-container">
                    <Logo
                      className="logo-main"
                      src={
                        homepageData?.level_62_logo_white?.url ||
                        homepageData?.level_62_logo_white
                      }
                      alt={
                        homepageData?.level_62_logo_white?.alt ||
                        'Level 62 Logo'
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="ascend-end-top">
                <h1
                  className={`end-title ${gameEndReason === 'completed' ? 'success' : ''}`}
                >
                  {endScreenContent.title}
                </h1>
                <p className="end-description">
                  {endScreenContent.description}
                </p>
              </div>
              <div className="results">
                <div className="result-item piped">
                  <div className="result-label">Miles Reached</div>
                  <div className="result-value">{floor}</div>
                </div>
                <div className="result-item">
                  <div className="result-label">Achieved Rank</div>
                  <div className="result-value">{rank}</div>
                </div>
              </div>
              <div className="game-navigation">
                <button className="btn-primary" onClick={handleBackToHome}>
                  Main Menu
                </button>
                <button className="btn-primary inverted" onClick={playAgain}>
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showExitPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <button
              className="popup-close"
              onClick={closeExitPopup}
            >
              <span>×</span>
            </button>
            <div className="popup-body">
              <h3>
                Are you sure you <br />
                want to exit?
              </h3>
              <p>You will lose your progress.</p>
              <button
                className="popup-button btn-primary inverted"
                onClick={handleBackToHome}
              >
                Yes, I'm sure
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Game1;
